import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { salesforceApi } from '@/api/salesforce';
import { agentsApi } from '@/api/agents';
import { queryKeys } from '@/lib/query-keys';
import { useTenantId } from '@/providers/TenantProvider';
import type {
  CreateSalesforceOrgInput,
  UpdateSalesforceOrgInput,
  UpdateSalesforceAuthInput,
  UpdateSalesforceRoutingInput,
  UUID,
} from '@/types';

export function useCreateSalesforceOrg() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSalesforceOrgInput) => salesforceApi.createOrg(tenantId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.orgs(tenantId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.connections(tenantId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.defaults(tenantId) });
    },
  });
}

export function useUpdateSalesforceOrg(orgId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSalesforceOrgInput) =>
      salesforceApi.updateOrg(tenantId, orgId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.orgs(tenantId) });
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.org(tenantId, orgId) });
    },
  });
}

export function useUpdateSalesforceAuth(orgId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSalesforceAuthInput) =>
      salesforceApi.updateAuth(tenantId, orgId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.auth(tenantId, orgId) });
    },
  });
}

export function useUpdateSalesforceRouting(orgId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSalesforceRoutingInput) =>
      salesforceApi.updateRouting(tenantId, orgId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.routing(tenantId, orgId) });
    },
  });
}

export function useDiscoverCallCenters(orgId: UUID, enabled = true) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.discovery(tenantId, orgId, 'call-centers'),
    queryFn: () => salesforceApi.discoverCallCenters(tenantId, orgId),
    enabled: !!tenantId && !!orgId && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useDiscoverVoiceFlows(orgId: UUID, enabled = true) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.discovery(tenantId, orgId, 'voice-flows'),
    queryFn: () => salesforceApi.discoverVoiceFlows(tenantId, orgId),
    enabled: !!tenantId && !!orgId && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSalesforceBindings(orgId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.bindings(tenantId, orgId),
    queryFn: () => salesforceApi.listBindings(tenantId, orgId),
    enabled: !!tenantId && !!orgId,
  });
}

export function useCreateOrgBinding(orgId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof salesforceApi.createBinding>[2]) =>
      salesforceApi.createBinding(tenantId, orgId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.bindings(tenantId, orgId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.connections(tenantId) });
    },
  });
}

export function useUpdateOrgBindingDefaults(orgId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof salesforceApi.updateBindingDefaults>[2]) =>
      salesforceApi.updateBindingDefaults(tenantId, orgId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.bindings(tenantId, orgId) });
    },
  });
}

export function useSalesforceRouting(orgId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.routing(tenantId, orgId),
    queryFn: () => salesforceApi.getRouting(tenantId, orgId),
    enabled: !!tenantId && !!orgId,
    retry: false,
  });
}

export function useSyncAgents(orgId?: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => agentsApi.syncFromSalesforce(tenantId, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.all(tenantId) });
    },
  });
}

export function useAgents(orgId?: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.agents.all(tenantId, orgId ? { salesforce_org_id: orgId } : {}),
    queryFn: () => agentsApi.list(tenantId, orgId ? { salesforce_org_id: orgId } : undefined),
    enabled: !!tenantId,
  });
}
